import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { usePhotographer } from '@context/PhotographerContext';
import { photographerAPI } from '@services/photographerApi';
import DashboardLayout from './layout/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import EventsManagement from './pages/EventsManagement';
import EventDetail from './pages/EventDetail';
import PhotoUpload from './pages/PhotoUpload';
import CustomersManagement from './pages/CustomersManagement';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import PricingManagement from './pages/PricingManagement';
import LoadingSpinner from '@components/common/LoadingSpinner';
import Alert from '@components/common/Alert';
import type { OnboardingStatus } from '@/types/photographer';
import { isDashboardPathAllowedDuringOnboarding } from './dashboardOnboardingGate';

const EventUploadRedirect: React.FC = () => {
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    photographerAPI
      .getEvents({ page: 1, limit: 1, sortBy: 'created', sortOrder: 'desc' })
      .then(res => {
        if (!active) return;
        const firstEvent = res.data[0];
        setTarget(firstEvent ? `/dashboard/events/${firstEvent.id}/upload` : '/dashboard/events');
      })
      .catch(() => {
        if (active) setTarget('/dashboard/events');
      });

    return () => {
      active = false;
    };
  }, []);

  if (!target) {
    return (
      <div className="min-h-[16rem] flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return <Navigate to={target} replace />;
};

const PhotographerDashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { photographer, loading, error, fetchPhotographer } = usePhotographer();
  const location = useLocation();
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const canViewDashboard =
    isAuthenticated && (user?.role === 'photographer' || user?.role === 'admin');

  // Fetch the onboarding status once per photographer at the dashboard shell
  // level. DashboardHome receives the cached value so navigating between
  // dashboard sibling routes does not refetch or briefly expose locked pages.
  useEffect(() => {
    if (!photographer?.id) {
      setOnboardingStatus(null);
      setOnboardingLoading(false);
      return;
    }

    let active = true;
    setOnboardingLoading(true);
    photographerAPI
      .getOnboardingStatus()
      .then(res => {
        if (active) setOnboardingStatus(res.data);
      })
      .catch(() => {
        // Treat onboarding as complete on error so photographers aren't stranded.
        if (active) setOnboardingStatus(null);
      })
      .finally(() => {
        if (active) setOnboardingLoading(false);
      });

    return () => {
      active = false;
    };
  }, [photographer?.id]);

  const onboardingIncomplete =
    onboardingStatus !== null && onboardingStatus.completed < onboardingStatus.total;

  // PhotographerProvider initializes its own data on mount; no fetch here
  // (adding one re-fired on every render because fetchPhotographer is
  // recreated by the provider, producing an unbounded fetch loop).

  // ProtectedRoute already enforces this; keep a defensive bounce in case
  // the component is rendered outside the /dashboard route guard.
  if (!canViewDashboard) {
    return <Navigate to="/login" replace />;
  }

  // Show loading state
  if (loading && !photographer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (onboardingLoading && photographer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (
    onboardingIncomplete &&
    !isDashboardPathAllowedDuringOnboarding(location.pathname)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show error state
  if (error && !photographer) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert
          type="error"
          title="Failed to load dashboard"
          message={error}
          actions={[
            {
              label: 'Retry',
              onClick: fetchPhotographer,
              variant: 'primary',
            },
          ]}
        />
      </div>
    );
  }

  return (
    <DashboardLayout>
      <Routes>
        {/* Dashboard Home */}
        <Route
          index
          element={
            <DashboardHome
              onboardingStatus={onboardingStatus}
              onboardingLoading={onboardingLoading}
            />
          }
        />

        {/* Events Management */}
        <Route path="events" element={<EventsManagement />} />
        {/* Uploads are event-scoped. The legacy/bare upload path selects a
            real event before entering PhotoUpload so "upload" is never used as
            an :eventId (#264). */}
        <Route path="events/upload" element={<EventUploadRedirect />} />
        <Route path="events/:eventId/upload" element={<PhotoUpload />} />
        <Route path="events/:eventId" element={<EventDetail />} />

        {/* Customers */}
        <Route path="customers" element={<CustomersManagement />} />

        {/* Analytics */}
        <Route path="analytics" element={<Analytics />} />

        {/* Pricing */}
        <Route path="pricing" element={<PricingManagement />} />

        {/* Settings */}
        <Route path="settings/*" element={<Settings />} />

        {/* Billing */}
        <Route path="billing" element={<Billing />} />

        {/* Catch all - redirect to dashboard home */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardLayout>
  );
};

export default PhotographerDashboard;
